import { OLAP_TOUR_TARGETS as T } from '@/components/olapV2/tour/olapTourTargets';
import i18n from '@/plugins/i18n';


// Guided-tour steps for the OLAP grid (WP-984) — the deep-dive companion to the two
// overview grid steps of the platform tour (steps/olap.js). It walks the grouping bar and
// the four sidebar tool panels, then how to read cells, run range math, act via the
// context menu, and save the view. Anchored to shared OLAP elements (olapTourTargets.js)
// so the same tour runs on every OLAP page.
//
// Sidebar-panel steps carry a `panel` id: OlapTour opens/activates that tool panel
// (gridApi.openToolPanel) before spotlighting, because the panel's content is not in the
// DOM while it is closed — so these steps can't be filtered by DOM presence at open time.
// They declare `available(ctx)` instead (dropped when the panel is in hiddenToolPanels).
// The sidebar's pre-tour state is snapshotted and restored on finish/close by OlapTour.
//
// title/description are pulled from the locale files under `tour.olapGrid.*` and exposed
// as getters so each read resolves against the active language — see steps/olap.js for the
// rationale.
const t = (key) => i18n.global.t(key);

const panelAvailable = (panel) => (ctx) => !((ctx?.hiddenToolPanels) || []).includes(panel);

export const olapGridTourSteps = [
  {
    key: 'intro',
    target: null, // no anchor → el-tour centers this as a welcome card
    get title() { return t('tour.olapGrid.intro.title'); },
    get description() { return t('tour.olapGrid.intro.description'); },
  },
  {
    key: 'grouping',
    // No panel hint — the grouping bar is always in the DOM while rowGroupPanelShow isn't
    // 'never', so it filters by anchor presence like the platform tour's steps do.
    target: T.gridGroupingBar,
    placement: 'bottom',
    get title() { return t('tour.olapGrid.grouping.title'); },
    get description() { return t('tour.olapGrid.grouping.description'); },
  },
  {
    key: 'dimensions',
    panel: 'dimensions',
    available: panelAvailable('dimensions'),
    target: T.gridOpenToolPanel,
    placement: 'left',
    get title() { return t('tour.olapGrid.dimensions.title'); },
    get description() { return t('tour.olapGrid.dimensions.description'); },
  },
  {
    key: 'metrics',
    panel: 'metrics',
    available: panelAvailable('metrics'),
    target: T.gridOpenToolPanel,
    placement: 'left',
    get title() { return t('tour.olapGrid.metrics.title'); },
    get description() { return t('tour.olapGrid.metrics.description'); },
  },
  {
    key: 'filters',
    panel: 'filters',
    available: panelAvailable('filters'),
    target: T.gridOpenToolPanel,
    placement: 'left',
    get title() { return t('tour.olapGrid.filters.title'); },
    get description() { return t('tour.olapGrid.filters.description'); },
  },
  {
    key: 'sorting',
    panel: 'sortStatus',
    available: panelAvailable('sortStatus'),
    target: T.gridSortPanel,
    placement: 'left',
    get title() { return t('tour.olapGrid.sorting.title'); },
    get description() { return t('tour.olapGrid.sorting.description'); },
  },
  {
    key: 'display',
    // Same panel as the previous step — keep it open and point at the Display toggles.
    panel: 'sortStatus',
    available: panelAvailable('sortStatus'),
    target: T.gridDisplayToggles,
    placement: 'left',
    get title() { return t('tour.olapGrid.display.title'); },
    get description() { return t('tour.olapGrid.display.description'); },
  },
  {
    key: 'aggregates',
    target: T.gridStatusBar,
    placement: 'top',
    get title() { return t('tour.olapGrid.aggregates.title'); },
    get description() { return t('tour.olapGrid.aggregates.description'); },
  },
  {
    key: 'actions',
    // Dynamic anchor mirroring the platform tour's grid step: with a chart the grid sits in
    // the lower viewport, so spotlight the whole table; on chart-less pages it fills the
    // viewport top-to-bottom and the popover would clip, so anchor to the header strip.
    target: () => (document.querySelector(T.chart)
      ? document.querySelector(T.grid)
      : document.querySelector(T.gridHeader)),
    placement: 'top',
    get title() { return t('tour.olapGrid.actions.title'); },
    get description() { return t('tour.olapGrid.actions.description'); },
  },
  {
    key: 'save',
    target: T.configs,
    placement: 'bottom',
    get title() { return t('tour.olapGrid.save.title'); },
    get description() { return t('tour.olapGrid.save.description'); },
  },
];
